import React, { useState } from "react";
import { AiOutlineInfo } from "react-icons/ai";
import { FaChevronDown, FaChevronUp, FaRoad } from "react-icons/fa";
import { MdRadar } from "react-icons/md";
import "../styles/Info.css";

const Info = ({ arco, radarSelected, geoData }) => {
  const [selectedRoadIndex, setSelectedRoadIndex] = useState(0);
  const [viewMode, setViewMode] = useState('road'); // 'road' or 'radar'
  const [expandedSections, setExpandedSections] = useState({
    general: true,
    technical: false,
    location: false,
  });

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  // Check if we have roads
  const hasRoads = arco && (Array.isArray(arco) ? arco.length > 0 : true);
  const hasRadar = radarSelected;

  // Handle case where only radar is selected (no roads)
  if (!hasRoads) {
    if (hasRadar) {
      return (
        <div className="info-wrapper">
          <div className="info-card">
            <div className="info-header">
              <div className="info-title">
                <MdRadar className="info-icon" />
                <h3>Info: Stazione di monitoraggio</h3>
              </div>
            </div>

            <div className="info-content">
              {/* General Information */}
              <div className="info-section">
                <button
                  className="info-section-header"
                  onClick={() => toggleSection("general")}
                >
                  <span>Informazioni Generali</span>
                  {expandedSections.general ? <FaChevronUp /> : <FaChevronDown />}
                </button>
                {expandedSections.general && (
                  <div className="info-section-content">
                    <div className="info-item">
                      <span className="info-label">Nome</span>
                      <span className="info-value">Multisensor Station - Genova</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Tipo</span>
                      <span className="info-value">Stazione di monitoraggio ambientale</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Stato</span>
                      <span className="info-value status-active">Attivo</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Fonte dati</span>
                      <span className="info-value">ambientali_finale_tutto</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Technical Information */}
              <div className="info-section">
                <button
                  className="info-section-header"
                  onClick={() => toggleSection("technical")}
                >
                  <span>Informazioni Tecniche</span>
                  {expandedSections.technical ? <FaChevronUp /> : <FaChevronDown />}
                </button>
                {expandedSections.technical && (
                  <div className="info-section-content">
                    <div className="info-item">
                      <span className="info-label">Sensori</span>
                      <span className="info-value">CO2, PM2.5, PM10, NO2, Temperatura, Umidità, Pressione</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Frequenza acquisizione</span>
                      <span className="info-value">Oraria</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Sensori accelerometrici</span>
                      <span className="info-value">Attivi (misura vibrazioni)</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Sensori audio</span>
                      <span className="info-value">Attivi (misura rumorosità)</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Rilevamento video</span>
                      <span className="info-value">Attivo (anomalie traffico)</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Location Information */}
              <div className="info-section">
                <button
                  className="info-section-header"
                  onClick={() => toggleSection("location")}
                >
                  <span>Posizione</span>
                  {expandedSections.location ? <FaChevronUp /> : <FaChevronDown />}
                </button>
                {expandedSections.location && (
                  <div className="info-section-content">
                    <div className="info-item">
                      <span className="info-label">Coordinate</span>
                      <span className="info-value">44.40838, 8.906963</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Città</span>
                      <span className="info-value">Genova</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Area</span>
                      <span className="info-value">Centro città</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="info-no-data">
        <AiOutlineInfo className="info-no-data-icon" />
        <p>Seleziona una strada o un radar sulla mappa per visualizzare le informazioni.</p>
      </div>
    );
  }

  // Handle road selection - support both array and single object
  const isMultipleRoads = Array.isArray(arco) && arco.length > 0;
  const isSingleRoad = !Array.isArray(arco) && arco;

  // Group by hash if it's an array
  let roads = [];
  if (isMultipleRoads) {
    const roadsByHash = {};
    arco.forEach((element) => {
      const hash = element.hash;
      if (!roadsByHash[hash]) {
        roadsByHash[hash] = [];
      }
      roadsByHash[hash].push(element);
    });

    // Get unique roads
    roads = Object.keys(roadsByHash).map((hash) => ({
      hash,
      elements: roadsByHash[hash],
      // Use most recent data point for display
      data: roadsByHash[hash][roadsByHash[hash].length - 1],
    }));
  } else if (isSingleRoad) {
    // Single road object
    roads = [{ hash: arco.hash, elements: [arco], data: arco }];
  }

  // Safety check: if roads is empty, return empty state
  if (roads.length === 0) {
    return (
      <div className="info-no-data">
        <AiOutlineInfo className="info-no-data-icon" />
        <p>Seleziona una strada sulla mappa per visualizzare le informazioni.</p>
      </div>
    );
  }

  // Render radar info component
  const renderRadarInfo = () => (
    <div className="info-card">
      <div className="info-header">
        <div className="info-title">
          <MdRadar className="info-icon" />
          <h3>Info: Stazione di monitoraggio</h3>
        </div>
      </div>

      <div className="info-content">
        {/* General Information */}
        <div className="info-section">
          <button
            className="info-section-header"
            onClick={() => toggleSection("general")}
          >
            <span>Informazioni Generali</span>
            {expandedSections.general ? <FaChevronUp /> : <FaChevronDown />}
          </button>
          {expandedSections.general && (
            <div className="info-section-content">
              <div className="info-item">
                <span className="info-label">Nome</span>
                <span className="info-value">Radar Station - Genova</span>
              </div>
              <div className="info-item">
                <span className="info-label">Tipo</span>
                <span className="info-value">Stazione di monitoraggio ambientale</span>
              </div>
              <div className="info-item">
                <span className="info-label">Stato</span>
                <span className="info-value status-active">Attivo</span>
              </div>
              <div className="info-item">
                <span className="info-label">Fonte dati</span>
                <span className="info-value">ambientali_finale_tutto</span>
              </div>
            </div>
          )}
        </div>

        {/* Technical Information */}
        <div className="info-section">
          <button
            className="info-section-header"
            onClick={() => toggleSection("technical")}
          >
            <span>Informazioni Tecniche</span>
            {expandedSections.technical ? <FaChevronUp /> : <FaChevronDown />}
          </button>
          {expandedSections.technical && (
            <div className="info-section-content">
              <div className="info-item">
                <span className="info-label">Sensori</span>
                <span className="info-value">CO2, PM2.5, PM10, NO2, Temperatura, Umidità, Pressione</span>
              </div>
              <div className="info-item">
                <span className="info-label">Frequenza acquisizione</span>
                <span className="info-value">Oraria</span>
              </div>
              <div className="info-item">
                <span className="info-label">Sensori accelerometrici</span>
                <span className="info-value">Attivi (misura vibrazioni)</span>
              </div>
              <div className="info-item">
                <span className="info-label">Sensori audio</span>
                <span className="info-value">Attivi (misura rumorosità)</span>
              </div>
              <div className="info-item">
                <span className="info-label">Rilevamento video</span>
                <span className="info-value">Attivo (anomalie traffico)</span>
              </div>
            </div>
          )}
        </div>

        {/* Location Information */}
        <div className="info-section">
          <button
            className="info-section-header"
            onClick={() => toggleSection("location")}
          >
            <span>Posizione</span>
            {expandedSections.location ? <FaChevronUp /> : <FaChevronDown />}
          </button>
          {expandedSections.location && (
            <div className="info-section-content">
              <div className="info-item">
                <span className="info-label">Coordinate</span>
                <span className="info-value">44.40838, 8.906963</span>
              </div>
              <div className="info-item">
                <span className="info-label">Città</span>
                <span className="info-value">Genova</span>
              </div>
              <div className="info-item">
                <span className="info-label">Area</span>
                <span className="info-value">Centro città</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // Render single road card
  const renderSingleRoadCard = (road, index) => {
    const roadData = road.data;

    // Get GeoJSON properties for this road by hash
    const getGeoProperties = () => {
      if (!geoData || !geoData.features) return null;
      const feature = geoData.features.find(f => f.properties.hash === roadData.hash);
      return feature ? feature.properties : null;
    };

    const geoProperties = getGeoProperties();

    // Calculate velocity percentiles from all data points for this road
    const calculateVelocityPercentiles = () => {
      const velocities = road.elements
        .map(el => {
          if (el.travel_time_in_seconds > 0 && el.length_in_meters) {
            return (el.length_in_meters / el.travel_time_in_seconds) * 3.6; // Convert m/s to km/h
          }
          return null;
        })
        .filter(v => v !== null)
        .sort((a, b) => a - b);

      if (velocities.length === 0) return { p25: null, p50: null, p75: null };

      const getPercentile = (arr, p) => {
        const indexVal = Math.ceil((p / 100) * arr.length) - 1;
        return Math.round(arr[Math.max(0, indexVal)] * 10) / 10; // Round to 1 decimal
      };

      return {
        p25: getPercentile(velocities, 25),
        p50: getPercentile(velocities, 50),
        p75: getPercentile(velocities, 75)
      };
    };

    const velocityPercentiles = calculateVelocityPercentiles();

    return (
      <div key={roadData.hash || index} className="info-card" style={{ marginBottom: roads.length > 1 ? '16px' : '0' }}>
        <div className="info-header">
          <div className="info-title">
            <FaRoad className="info-icon" />
            <h3>Info: Strada {roads.length > 1 ? index + 1 : ''}</h3>
          </div>
        </div>

        <div className="info-content">
          {/* General Information */}
          <div className="info-section">
            <button
              className="info-section-header"
              onClick={() => toggleSection("general")}
            >
              <span>Informazioni generali</span>
              {expandedSections.general ? <FaChevronUp /> : <FaChevronDown />}
            </button>
            {expandedSections.general && (
              <div className="info-section-content">
                <div className="info-item">
                  <span className="info-label">Nome</span>
                  <span className="info-value">
                    {geoProperties?.StreetName || roadData.StreetName || roadData.name || "N/A"}
                  </span>
                </div>
                <div className="info-item">
                  <span className="info-label">Id</span>
                  <span className="info-value info-hash">{roadData.hash || "N/A"}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Lunghezza</span>
                  <span className="info-value">
                    {roadData.length_in_meters !== undefined
                      ? `${roadData.length_in_meters} m`
                      : geoProperties?.Length !== undefined
                      ? `${Math.round(geoProperties.Length)} m`
                      : "N/A"}
                  </span>
                </div>
                <div className="info-item">
                  <span className="info-label">Limite di velocità</span>
                  <span className="info-value">
                    {geoProperties?.SpeedLimit !== undefined
                      ? `${geoProperties.SpeedLimit} km/h`
                      : roadData.SpeedLimit !== undefined
                      ? `${roadData.SpeedLimit} km/h`
                      : roadData.speed_limit !== undefined
                      ? `${roadData.speed_limit} km/h`
                      : "N/A"}
                  </span>
                </div>
                <div className="info-item">
                  <span className="info-label">FRC (Functional Road Class)</span>
                  <span className="info-value">
                    {roadData.FRC !== undefined
                      ? `Livello ${Math.round(roadData.FRC)}`
                      : geoProperties?.FRC !== undefined
                      ? `Livello ${Math.round(geoProperties.FRC)}`
                      : "N/A"}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Historical Data */}
          <div className="info-section">
            <button
              className="info-section-header"
              onClick={() => toggleSection("technical")}
            >
              <span>Dati Storici</span>
              {expandedSections.technical ? <FaChevronUp /> : <FaChevronDown />}
            </button>
            {expandedSections.technical && (
              <div className="info-section-content">
                <div className="info-item">
                  <span className="info-label">Tempo medio di percorrenza</span>
                  <span className="info-value">
                    {roadData.historic_traffic_travel_time_in_seconds !== undefined
                      ? `${roadData.historic_traffic_travel_time_in_seconds} s`
                      : "N/A"}
                  </span>
                </div>
                <div className="info-item">
                  <span className="info-label">Velocità media (25° percentile)</span>
                  <span className="info-value">
                    {velocityPercentiles.p25 !== null
                      ? `${velocityPercentiles.p25} km/h`
                      : "N/A"}
                  </span>
                </div>
                <div className="info-item">
                  <span className="info-label">Velocità media (50° percentile)</span>
                  <span className="info-value">
                    {velocityPercentiles.p50 !== null
                      ? `${velocityPercentiles.p50} km/h`
                      : "N/A"}
                  </span>
                </div>
                <div className="info-item">
                  <span className="info-label">Velocità media (75° percentile)</span>
                  <span className="info-value">
                    {velocityPercentiles.p75 !== null
                      ? `${velocityPercentiles.p75} km/h`
                      : "N/A"}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Live Data */}
          <div className="info-section">
            <button
              className="info-section-header"
              onClick={() => toggleSection("location")}
            >
              <span>Dati Live</span>
              {expandedSections.location ? <FaChevronUp /> : <FaChevronDown />}
            </button>
            {expandedSections.location && (
              <div className="info-section-content">
                <div className="info-item">
                  <span className="info-label">Presenza incidenti</span>
                  <span className={`info-value ${
                    roadData.live_traffic_incidents_travel_time_in_seconds !== undefined &&
                    roadData.travel_time_in_seconds !== undefined &&
                    roadData.live_traffic_incidents_travel_time_in_seconds !== roadData.travel_time_in_seconds
                      ? "status-inactive"
                      : "status-active"
                  }`}>
                    {roadData.live_traffic_incidents_travel_time_in_seconds !== undefined &&
                    roadData.travel_time_in_seconds !== undefined
                      ? roadData.live_traffic_incidents_travel_time_in_seconds !== roadData.travel_time_in_seconds
                        ? `SÌ (${roadData.live_traffic_incidents_travel_time_in_seconds}s)`
                        : "NO"
                      : "NO"}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Render road info component (shows selected road when multiple)
  const renderRoadInfo = () => {
    const currentRoad = roads[selectedRoadIndex];
    return renderSingleRoadCard(currentRoad, selectedRoadIndex);
  };

  return (
    <div className="info-wrapper">
      {/* Mode selector when both road and radar are selected */}
      {hasRoads && hasRadar && (
        <div className="info-road-selector" style={{ marginBottom: '12px' }}>
          <button
            onClick={() => setViewMode('road')}
            style={{
              padding: '8px 16px',
              borderRadius: '6px',
              border: viewMode === 'road' ? '2px solid #A1E430' : '1px solid #ddd',
              background: viewMode === 'road' ? '#A1E43020' : 'white',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: viewMode === 'road' ? 'bold' : 'normal',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <FaRoad size={14} />
            <span>Strada</span>
          </button>
          <button
            onClick={() => setViewMode('radar')}
            style={{
              padding: '8px 16px',
              borderRadius: '6px',
              border: viewMode === 'radar' ? '2px solid #1890ff' : '1px solid #ddd',
              background: viewMode === 'radar' ? '#1890ff20' : 'white',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: viewMode === 'radar' ? 'bold' : 'normal',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <MdRadar size={16} />
            <span>Radar</span>
          </button>
        </div>
      )}

      {/* Road selector for multiple road selections */}
      {viewMode === 'road' && roads.length > 1 && (
        <div className="info-road-selector">
          <button
            onClick={() => setSelectedRoadIndex(0)}
            style={{
              padding: '6px 12px',
              borderRadius: '6px',
              border: selectedRoadIndex === 0 ? '2px solid #A1E430' : '1px solid #ddd',
              background: selectedRoadIndex === 0 ? '#A1E43020' : 'white',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: selectedRoadIndex === 0 ? 'bold' : 'normal'
            }}
          >
            Strada 1
          </button>
          <button
            onClick={() => setSelectedRoadIndex(1)}
            style={{
              padding: '6px 12px',
              borderRadius: '6px',
              border: selectedRoadIndex === 1 ? '2px solid #8B7FD9' : '1px solid #ddd',
              background: selectedRoadIndex === 1 ? '#8B7FD920' : 'white',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: selectedRoadIndex === 1 ? 'bold' : 'normal'
            }}
          >
            Strada 2
          </button>
        </div>
      )}

      {/* Display the selected view */}
      {viewMode === 'radar' && hasRadar ? renderRadarInfo() : renderRoadInfo()}
    </div>
  );
};

export default Info;
